import React from "react";
import { BookOpen, Calendar, Hash, Lock, Plus, PlusCircle, Trash2, X } from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";

/**
 * O'level results, captured as SITTINGS — the same way the probationer
 * application asks for them (see application/components/steps/Step3OLevel).
 *
 * A candidate sat an exam, not a subject: one board, one year, one exam number
 * and one scanned result slip, with the subjects taken in that sitting inside
 * it. Up to three sittings may be combined. Asking subject-by-subject, each
 * with its own board and its own upload, made a candidate re-enter the same
 * sitting five times and upload the same slip five times.
 *
 * The table underneath is still one row per subject — it is shared with the
 * TPC/PCI application form — so the server expands a sitting on save and
 * regroups on read. That is a storage detail; it is not how anyone is asked.
 */

const STORAGE_ROOT = `${BASE_URL.replace(/\/api\/?$/, "")}/storage`;

export const storageUrl = (p?: string | null) =>
  !p ? null : /^https?:\/\//i.test(p) ? p : `${STORAGE_ROOT}/${p.replace(/^\/+/, "")}`;

export type SubjectRow = { subject: string; grade: string };

export type Sitting = {
  exam_type: string;
  exam_year: string;
  exam_number: string;
  /** A scan already on file — echoed back so saving does not detach it. */
  result_file?: string | null;
  /** A newly chosen scan, not yet uploaded. */
  file?: File | null;
  subjects: SubjectRow[];
};

/** Sittings a candidate may combine, matching the probationer form. */
export const MAX_SITTINGS = 3;

/** The largest scan the server will take. Caught here so it is caught early. */
const MAX_FILE_MB = 10;

const EXAM_TYPES = [
  { value: "waec", label: "WAEC (WASSCE)" },
  { value: "neco", label: "NECO (SSCE)" },
  { value: "nabteb", label: "NABTEB" },
  { value: "other", label: "Other" },
];

const GRADES = ["A1", "B2", "B3", "C4", "C5", "C6", "D7", "E8", "F9"];

/** Subjects the Institute requires in every sitting; not editable or removable. */
const COMPULSORY = ["mathematics", "english", "physics"] as const;

const normalize = (v: string) => v.trim().toLowerCase();
const isCompulsory = (subject: string) =>
  (COMPULSORY as readonly string[]).includes(normalize(subject));

export function blankSitting(): Sitting {
  return {
    exam_type: "waec",
    exam_year: "",
    exam_number: "",
    result_file: null,
    file: null,
    subjects: [
      { subject: "Mathematics", grade: "" },
      { subject: "English", grade: "" },
      { subject: "Physics", grade: "" },
      { subject: "", grade: "" },
      { subject: "", grade: "" },
    ],
  };
}

/**
 * Server rows (already grouped into sittings) into editable form.
 *
 * The three compulsory subjects are floated to the top in a fixed order, so a
 * prefilled sitting reads the same as a fresh one and the locked rows are
 * always where the candidate expects them.
 */
export function toEditableSitting(s: any): Sitting {
  const incoming: SubjectRow[] = (s?.subjects ?? []).map((x: any) => ({
    subject: x.subject ?? "",
    grade: String(x.grade ?? "").toUpperCase(),
  }));

  const pick = (name: string) =>
    incoming.find((x) => normalize(x.subject) === name)?.grade ?? "";

  const rest = incoming.filter((x) => !isCompulsory(x.subject));

  const subjects: SubjectRow[] = [
    { subject: "Mathematics", grade: pick("mathematics") },
    { subject: "English", grade: pick("english") },
    { subject: "Physics", grade: pick("physics") },
    ...rest,
  ];

  while (subjects.length < 5) subjects.push({ subject: "", grade: "" });

  return {
    exam_type: s?.exam_type || "waec",
    exam_year: String(s?.exam_year ?? ""),
    exam_number: s?.exam_number ?? "",
    result_file: s?.result_file ?? null,
    file: null,
    subjects,
  };
}

/**
 * The first thing wrong with these sittings, or null.
 *
 * Same rules as the probationer form: five subjects with grades, the three
 * compulsory ones among them, no duplicates, and a scanned result for each
 * sitting. Checked here so the candidate is told which sitting and what,
 * rather than being handed a generic refusal after a round trip.
 */
export function validateSittings(sittings: Sitting[]): string | null {
  if (sittings.length === 0) return "Add your O'level result.";

  for (let i = 0; i < sittings.length; i++) {
    const s = sittings[i];
    const n = i + 1;

    if (!s.exam_year) return `Sitting ${n}: choose the exam year.`;
    if (!s.exam_number.trim()) return `Sitting ${n}: the exam number is required.`;

    const filled = s.subjects.filter((x) => x.subject.trim() && x.grade);
    if (filled.length < 5) return `Sitting ${n}: enter at least 5 subjects with grades.`;

    const keys = filled.map((x) => normalize(x.subject));
    for (const required of COMPULSORY) {
      if (!keys.includes(required)) {
        const label = required[0].toUpperCase() + required.slice(1);
        return `Sitting ${n}: ${label} is compulsory.`;
      }
    }
    if (new Set(keys).size !== keys.length) return `Sitting ${n}: remove the duplicate subject.`;

    if (!s.file && !s.result_file) {
      return `Sitting ${n}: upload a scanned copy of the result.`;
    }
  }

  return null;
}

export default function OlevelSittings({
  sittings, onChange, disabled, onError,
}: {
  sittings: Sitting[];
  onChange: (next: Sitting[]) => void;
  disabled?: boolean;
  /** Raised for a file the server would reject, so it is caught before saving. */
  onError?: (message: string) => void;
}) {
  const setSitting = (i: number, patch: Partial<Sitting>) =>
    onChange(sittings.map((s, x) => (x === i ? { ...s, ...patch } : s)));

  const setSubject = (i: number, si: number, patch: Partial<SubjectRow>) =>
    onChange(
      sittings.map((s, x) =>
        x === i
          ? { ...s, subjects: s.subjects.map((r, j) => (j === si ? { ...r, ...patch } : r)) }
          : s,
      ),
    );

  const acceptScan = (file: File | null): File | null => {
    if (!file) return null;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      onError?.(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The maximum is ${MAX_FILE_MB} MB — please upload a smaller or compressed scan.`,
      );
      return null;
    }
    return file;
  };

  const years = Array.from(
    { length: new Date().getFullYear() - 1979 },
    (_, k) => new Date().getFullYear() - k,
  );

  return (
    <div className="space-y-5">
      {sittings.map((s, i) => (
        <div key={i} className="border border-gray-200 rounded-2xl p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest">
              Exam sitting {i + 1}
            </span>
            {sittings.length > 1 && !disabled && (
              <button
                type="button"
                onClick={() => onChange(sittings.filter((_, x) => x !== i))}
                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                title="Remove this sitting"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2">
                <BookOpen className="w-3 h-3" /> Exam type
              </label>
              <select
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
                value={s.exam_type}
                disabled={disabled}
                onChange={(e) => setSitting(i, { exam_type: e.target.value })}
              >
                {EXAM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2">
                <Calendar className="w-3 h-3" /> Exam year <span className="text-rose-500">*</span>
              </label>
              <select
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
                value={s.exam_year}
                disabled={disabled}
                onChange={(e) => setSitting(i, { exam_year: e.target.value })}
              >
                <option value="">Select year</option>
                {years.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2">
                <Hash className="w-3 h-3" /> Exam number <span className="text-rose-500">*</span>
              </label>
              <input
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
                value={s.exam_number}
                disabled={disabled}
                placeholder="e.g. 4123456789"
                onChange={(e) => setSitting(i, { exam_number: e.target.value })}
              />
            </div>
          </div>

          {/* One scan for the whole sitting — it is one result slip. */}
          <div className="rounded-2xl p-4 border border-gray-100 bg-gray-50/50 space-y-2 mb-5">
            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest block">
              Scanned copy of result <span className="text-rose-500">*</span>
            </label>
            {!disabled && (
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="w-full text-xs"
                onChange={(e) => {
                  const f = acceptScan(e.target.files?.[0] ?? null);
                  if (!f) { e.target.value = ""; return; }
                  setSitting(i, { file: f });
                }}
              />
            )}
            {s.file ? (
              <p className="text-[11px] text-emerald-600 font-medium">Selected: {s.file.name}</p>
            ) : storageUrl(s.result_file) ? (
              <a
                href={storageUrl(s.result_file)!}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-indigo-600 hover:underline font-medium"
              >
                View uploaded copy
              </a>
            ) : (
              <p className="text-[11px] text-gray-400">
                PDF, JPG or PNG up to {MAX_FILE_MB} MB. Required for each sitting.
              </p>
            )}
          </div>

          <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
              <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                Subject grades
              </h4>
              {!disabled && (
                <button
                  type="button"
                  onClick={() =>
                    setSitting(i, { subjects: [...s.subjects, { subject: "", grade: "" }] })
                  }
                  className="flex items-center gap-1.5 text-xs font-bold text-gray-900 hover:text-gray-600"
                >
                  <PlusCircle className="w-4 h-4" /> Add subject
                </button>
              )}
            </div>

            <div className="space-y-3">
              {s.subjects.map((row, si) => {
                const locked = isCompulsory(row.subject);
                return (
                  <div key={si} className="grid grid-cols-12 gap-2 sm:gap-3 items-center">
                    <div className="col-span-7 relative">
                      <input
                        className={`w-full p-2.5 border rounded-xl text-sm outline-none ${
                          locked
                            ? "bg-white border-gray-100 text-gray-400 cursor-not-allowed font-medium"
                            : "bg-white border-gray-200 focus:ring-2 focus:ring-gray-100"
                        }`}
                        value={row.subject}
                        placeholder="e.g. Biology"
                        disabled={disabled || locked}
                        onChange={(e) => setSubject(i, si, { subject: e.target.value })}
                      />
                      {locked && (
                        <Lock className="absolute right-3 top-3 w-3 h-3 text-gray-300" />
                      )}
                    </div>

                    <div className="col-span-4">
                      <select
                        className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-gray-100"
                        value={row.grade}
                        disabled={disabled}
                        onChange={(e) => setSubject(i, si, { grade: e.target.value })}
                      >
                        <option value="">Grade</option>
                        {GRADES.map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-1 flex justify-center">
                      {s.subjects.length > 5 && !locked && !disabled && (
                        <button
                          type="button"
                          onClick={() =>
                            setSitting(i, { subjects: s.subjects.filter((_, j) => j !== si) })
                          }
                          className="p-1.5 text-gray-400 hover:text-rose-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {sittings.length < MAX_SITTINGS && !disabled && (
        <button
          type="button"
          onClick={() => onChange([...sittings, blankSitting()])}
          className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all"
        >
          <Plus className="w-4 h-4" />
          Combine another sitting
          <span className="ml-1 text-gray-400 font-normal">
            ({sittings.length}/{MAX_SITTINGS})
          </span>
        </button>
      )}
    </div>
  );
}
